import { useEffect, useRef, useState } from "react";

import { logDirectorViewEvent } from "./directorViewDebug";
import type { MediaUrlSource } from "./useMediaUrl";
import { useMediaUrl } from "./useMediaUrl";
import type { WaveformPeaks } from "./waveformPeaks";
import { computePeaks } from "./waveformPeaks";

// ---------------------------------------------------------------------------
// useAudioPeaks / useAudioDurations — getting audio facts out of delivered
// media without shipping an audio library (DIR-003).
//
// Two very different costs, so two hooks:
//
//   useAudioPeaks     downloads the whole MP4 and decodes it to draw the mix
//                     waveform. Expensive, so it is strictly opt-in: nothing
//                     happens until the panel is actually expanded.
//   useAudioDurations reads only the metadata of the per-line narration MP3s
//                     via <audio preload="metadata">. Cheap, and it is all the
//                     narration tier ladder needs to measure line lengths —
//                     decoding dozens of clips to learn their durations would
//                     be wasteful.
//
// Everything is cancellable: collapsing the panel or leaving the page aborts
// in-flight fetches and stops pending decodes from touching state.
// ---------------------------------------------------------------------------

export type AudioPeaksStatus =
  | "idle"
  | "loading"
  | "ready"
  | "unsupported"
  | "error";

export type AudioPeaksState = {
  status: AudioPeaksStatus;
  peaks: WaveformPeaks | null;
  durationSeconds: number | null;
  error: string | null;
};

type PeaksCacheEntry = {
  peaks: WaveformPeaks;
  durationSeconds: number;
};

/** Peak sets are ~32 KB each; a handful covers navigating between echoes. */
const MAX_CACHED_PEAKS = 6;
const peaksCache = new Map<string, PeaksCacheEntry>();

/** Parallel metadata probes. Enough to be quick, few enough to stay polite. */
const DURATION_PROBE_CONCURRENCY = 6;

function cacheKey(source: MediaUrlSource | null | undefined): string | null {
  if (!source) return null;
  if (source.storage_path) {
    return `${source.storage_bucket ?? ""}|${source.storage_path}`;
  }
  return source.url ?? null;
}

function readCachedPeaks(key: string): PeaksCacheEntry | null {
  const cached = peaksCache.get(key);
  if (!cached) return null;
  // Refresh insertion order so the size cap behaves as an LRU.
  peaksCache.delete(key);
  peaksCache.set(key, cached);
  return cached;
}

function writeCachedPeaks(key: string, entry: PeaksCacheEntry): void {
  peaksCache.set(key, entry);
  while (peaksCache.size > MAX_CACHED_PEAKS) {
    const oldest = peaksCache.keys().next();
    if (oldest.done) break;
    peaksCache.delete(oldest.value);
  }
}

/** Exposed for tests; also lets a remake drop a stale render's waveform. */
export function clearAudioPeaksCache(): void {
  peaksCache.clear();
}

type AudioContextConstructor = new () => AudioContext;

function resolveAudioContext(): AudioContextConstructor | null {
  if (typeof window === "undefined") return null;
  const scope = window as unknown as {
    AudioContext?: AudioContextConstructor;
    webkitAudioContext?: AudioContextConstructor;
  };
  return scope.AudioContext ?? scope.webkitAudioContext ?? null;
}

async function decode(
  context: AudioContext,
  data: ArrayBuffer,
): Promise<AudioBuffer> {
  // Safari still exposes only the callback form.
  return new Promise<AudioBuffer>((resolve, reject) => {
    const maybePromise = context.decodeAudioData(data, resolve, reject);
    if (maybePromise && typeof maybePromise.then === "function") {
      maybePromise.then(resolve, reject);
    }
  });
}

/**
 * Decode `source` and extract its waveform peaks. Inert until `enabled`.
 *
 * Failure is never fatal to the caller: an unsupported container or a blocked
 * fetch resolves to a status the panel renders as "no waveform", leaving the
 * narration blocks and transcript working.
 */
export function useAudioPeaks(
  source: MediaUrlSource | null | undefined,
  options: { enabled: boolean },
): AudioPeaksState {
  const { enabled } = options;
  const media = useMediaUrl(enabled ? source : null);
  const key = cacheKey(source);
  const [state, setState] = useState<AudioPeaksState>({
    status: "idle",
    peaks: null,
    durationSeconds: null,
    error: null,
  });
  // One signed-URL retry per source, matching the viewer's refresh-once posture.
  const retriedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !key) {
      setState({
        status: "idle",
        peaks: null,
        durationSeconds: null,
        error: null,
      });
      return;
    }

    const cached = readCachedPeaks(key);
    if (cached) {
      setState({
        status: "ready",
        peaks: cached.peaks,
        durationSeconds: cached.durationSeconds,
        error: null,
      });
      return;
    }

    const url = media.url;
    if (!url) {
      setState((current) =>
        current.status === "loading"
          ? current
          : { ...current, status: "loading", error: null },
      );
      return;
    }

    const AudioContextCtor = resolveAudioContext();
    if (!AudioContextCtor) {
      setState({
        status: "unsupported",
        peaks: null,
        durationSeconds: null,
        error: "Web Audio is unavailable in this browser",
      });
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    let context: AudioContext | null = null;

    setState({
      status: "loading",
      peaks: null,
      durationSeconds: null,
      error: null,
    });

    (async () => {
      const startedAt = performance.now();
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) {
          // A stale signature is the common case — re-sign once, then give up.
          if (
            (response.status === 403 || response.status === 404) &&
            retriedRef.current !== key
          ) {
            retriedRef.current = key;
            logDirectorViewEvent("peaks.retry", {
              key,
              status: response.status,
            });
            media.refresh();
            return;
          }
          throw new Error(`media request failed (${response.status})`);
        }
        const data = await response.arrayBuffer();
        if (cancelled) return;
        const fetchedAt = performance.now();

        context = new AudioContextCtor();
        const buffer = await decode(context, data);
        if (cancelled) return;

        const channels: Float32Array[] = [];
        for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
          channels.push(buffer.getChannelData(channel));
        }
        const peaks = computePeaks(channels);
        const durationSeconds = buffer.duration;
        // The waveform-path receipt: proves the fetch got CORS-clean bytes and
        // the browser could demux this container, with the real costs.
        logDirectorViewEvent("peaks.ready", {
          key,
          bytes: data.byteLength,
          fetchMs: Math.round(fetchedAt - startedAt),
          decodeMs: Math.round(performance.now() - fetchedAt),
          channels: buffer.numberOfChannels,
          durationSeconds: Math.round(durationSeconds * 100) / 100,
        });
        // Drop the decoded audio before storing: peaks are three orders of
        // magnitude smaller and are all the renderer needs.
        writeCachedPeaks(key, { peaks, durationSeconds });
        setState({ status: "ready", peaks, durationSeconds, error: null });
      } catch (error) {
        if (cancelled || controller.signal.aborted) return;
        const message =
          error instanceof Error ? error.message : "audio decode failed";
        // The failure receipt: distinguishes a CORS/fetch failure from an
        // undecodable container at a glance.
        logDirectorViewEvent("peaks.error", { key, message });
        setState({
          status: "error",
          peaks: null,
          durationSeconds: null,
          error: message,
        });
      } finally {
        void context?.close().catch(() => undefined);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // `media.refresh` is stable per source; re-running on url change is the
    // point (that is how the single retry proceeds).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key, media.url]);

  useEffect(() => {
    if (media.status === "error" && enabled) {
      setState({
        status: "error",
        peaks: null,
        durationSeconds: null,
        error: media.error ?? "media unavailable",
      });
    }
  }, [media.status, media.error, enabled]);

  return state;
}

// ---------------------------------------------------------------------------
// Per-line durations
// ---------------------------------------------------------------------------

export type AudioDurationSource = {
  index: number;
  source: MediaUrlSource;
};

/**
 * Probe one clip's duration without decoding it. `preload="metadata"` fetches
 * only the container header, which is a few kilobytes even for a long file.
 */
function probeDuration(
  url: string,
  signal: AbortSignal,
): Promise<number | null> {
  return new Promise((resolve) => {
    if (typeof document === "undefined") {
      resolve(null);
      return;
    }
    const audio = document.createElement("audio");
    let settled = false;
    const finish = (value: number | null) => {
      if (settled) return;
      settled = true;
      audio.removeAttribute("src");
      audio.load();
      resolve(value);
    };
    audio.preload = "metadata";
    audio.addEventListener("loadedmetadata", () => {
      finish(Number.isFinite(audio.duration) ? audio.duration : null);
    });
    audio.addEventListener("error", () => finish(null));
    signal.addEventListener("abort", () => finish(null), { once: true });
    audio.src = url;
  });
}

/**
 * Measure the per-line narration clips. Feeds the narration tier ladder's
 * `voiceover_files` tier, which replays the composer's placement policy over
 * real durations instead of a words-per-minute guess.
 *
 * Signing happens through the same dashboard endpoint as everything else, so
 * this is a plain sequential walk with a small concurrency cap rather than a
 * hook per line (their count varies per echo).
 */
export function useAudioDurations(
  sources: AudioDurationSource[],
  options: {
    enabled: boolean;
    signUrl: (source: MediaUrlSource) => Promise<string | null>;
  },
): {
  durations: ReadonlyMap<number, number>;
  status: "idle" | "loading" | "ready";
} {
  const { enabled, signUrl } = options;
  const [durations, setDurations] = useState<ReadonlyMap<number, number>>(
    new Map(),
  );
  const [status, setStatus] = useState<"idle" | "loading" | "ready">("idle");
  const signRef = useRef(signUrl);
  signRef.current = signUrl;

  const fingerprint = sources
    .map(
      (entry) =>
        `${entry.index}:${entry.source.storage_path ?? entry.source.url ?? ""}`,
    )
    .join("|");

  useEffect(() => {
    if (!enabled || sources.length === 0) {
      setStatus("idle");
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    setStatus("loading");
    const startedAt = performance.now();

    (async () => {
      const measured = new Map<number, number>();
      const queue = [...sources];

      const worker = async () => {
        while (queue.length > 0 && !cancelled) {
          const entry = queue.shift();
          if (!entry) break;
          try {
            const url = await signRef.current(entry.source);
            if (!url || cancelled) continue;
            const duration = await probeDuration(url, controller.signal);
            if (duration != null && duration > 0) {
              measured.set(entry.index, duration);
            }
          } catch {
            // A single unreadable clip just means that line keeps its estimate.
          }
        }
      };

      await Promise.all(
        Array.from(
          { length: Math.min(DURATION_PROBE_CONCURRENCY, sources.length) },
          worker,
        ),
      );

      if (cancelled) return;
      // The clip-measurement receipt: how many of the per-line narration MP3s
      // actually yielded a length (a shortfall means those lines fall back to
      // the words-per-minute estimate).
      logDirectorViewEvent("durations.measured", {
        measured: measured.size,
        requested: sources.length,
        ms: Math.round(performance.now() - startedAt),
      });
      setDurations(measured);
      setStatus("ready");
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, fingerprint]);

  return { durations, status };
}
